import React from 'react';
import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity, Platform } from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';

import { useAuthContext } from '../context/AuthContext';
import type { RootStackParamList, UserTabParamList } from '../types';

import LoginScreen from '../screens/LoginScreen';
import VerifyEmailScreen from '../screens/VerifyEmailScreen';
import HomeScreen from '../screens/HomeScreen';
import ProfileDetailScreen from '../screens/ProfileDetailScreen';
import EditProfileScreen from '../screens/EditProfileScreen';
import MyProfileScreen from '../screens/MyProfileScreen';
import PhotoScreen from '../screens/PhotoScreen';
import SearchScreen from '../screens/SearchScreen';
import PreferencesScreen from '../screens/PreferencesScreen';
import MembershipScreen from '../screens/MembershipScreen';
import InterestsScreen from '../screens/InterestsScreen';
import HelpScreen from '../screens/HelpScreen';
import OnboardingScreen from '../screens/OnboardingScreen';
import BlockedProfilesScreen from '../screens/BlockedProfilesScreen';

const Stack = createStackNavigator<RootStackParamList>();
const UserTab = createBottomTabNavigator<UserTabParamList>();

// Deep link config:
//   Custom scheme:  jeevansanyog://profile/JS-1234
//   Universal link: https://jeevansanyog.com/profile/JS-1234  (requires apple-app-site-association + assetlinks.json on server)
const linking = {
  prefixes: ['jeevansanyog://', 'https://jeevansanyog.com', 'https://www.jeevansanyog.com'],
  config: {
    screens: {
      ProfileDetail: 'profile/:profileCode',
    },
  },
};

const COLORS = { active: '#C2185B', inactive: '#999' };

type IconMap = Record<string, string>;

function UserTabs(): React.JSX.Element {
  return (
    <UserTab.Navigator
      screenOptions={({ route }) => ({
        headerShown: false,
        tabBarActiveTintColor: COLORS.active,
        tabBarInactiveTintColor: COLORS.inactive,
        tabBarIcon: ({ color, size }: { color: string; size: number }) => {
          const icons: IconMap = {
            Home: 'home-outline',
            Search: 'search-outline',
            Interests: 'pulse-outline',
            Membership: 'diamond-outline',
            MyProfile: 'person-outline',
          };
          return <Icon name={icons[route.name] ?? 'ellipse-outline'} size={size} color={color} />;
        },
      })}>
      <UserTab.Screen name="Home" component={HomeScreen as React.ComponentType<any>} options={{ tabBarLabel: 'Profiles' }} />
      <UserTab.Screen name="Search" component={SearchScreen} />
      <UserTab.Screen name="Interests" component={InterestsScreen as React.ComponentType<any>} options={{ tabBarLabel: 'Activity' }} />
      <UserTab.Screen name="Membership" component={MembershipScreen} />
      <UserTab.Screen name="MyProfile" component={MyProfileScreen} options={{ tabBarLabel: 'My Profile' }} />
    </UserTab.Navigator>
  );
}


// If Auth0's initial silent-auth check (or the post-login profile lookup)
// never settles — e.g. a stalled network request — isLoading/hasProfile
// would otherwise leave the user on the spinner forever with no way out.
// After LOADING_TIMEOUT_MS, fall back to the Login screen on web (native
// hasn't shown this failure mode).
const LOADING_TIMEOUT_MS = 9000;

function LoadingGate(): React.JSX.Element {
  const [stuck, setStuck] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setStuck(true), LOADING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  // Whatever got stuck (Auth0's initial check, or the post-login profile
  // lookup), falling back to the real Login screen is a safe universal
  // recovery: it's the same Google/Apple buttons the user already
  // recognizes, and login() passes straight through without asking for
  // credentials again if there's still a valid session underneath.
  if (stuck && Platform.OS === 'web') {
    return <LoginScreen />;
  }

  return (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff' }}>
      <ActivityIndicator size="large" color={COLORS.active} />
    </View>
  );
}

function ReloginScreen(): React.JSX.Element {
  const { relogin } = useAuthContext();
  return (
    <View style={s.adminBlock}>
      <Icon name="link-outline" size={60} color={COLORS.active} />
      <Text style={s.adminTitle}>Session Expired</Text>
      <Text style={s.adminSub}>
        Your accounts have been linked.{'\n'}
        Please sign in again to access your profile.
      </Text>
      <TouchableOpacity style={s.reloginBtn} onPress={relogin}>
        <Text style={s.reloginBtnText}>Go to Sign In</Text>
      </TouchableOpacity>
    </View>
  );
}

export default function AppNavigator(): React.JSX.Element {
  const { user, emailNotVerified, hasProfile, needsRelogin, isLoading } = useAuthContext();

  // On web, NavigationContainer's URL-linking sync races with Auth0's
  // redirect-callback handling: if the navigator mounts and resolves the
  // current path first, it rewrites the URL (e.g. to "/Login") and wipes the
  // "?code=&state=" query string before Auth0Provider gets a chance to read
  // it. Holding off until Auth0's own initial resolution (isLoading) is done
  // avoids the race — this is Auth0's own documented pattern for use with
  // client-side routers.
  if (isLoading) {
    return <LoadingGate />;
  }

  if (user && !emailNotVerified && !needsRelogin && hasProfile === null) {
    return <LoadingGate />;
  }

  return (
    <NavigationContainer linking={linking}>
      <Stack.Navigator screenOptions={{ headerShown: false }}>
        {emailNotVerified ? (
          <Stack.Screen name="Login" component={VerifyEmailScreen} />
        ) : !user ? (
          <Stack.Screen name="Login" component={LoginScreen} />
        ) : needsRelogin ? (
          <Stack.Screen name="Login" component={ReloginScreen} />
        ) : hasProfile === false ? (
          <Stack.Screen name="Onboarding" component={OnboardingScreen} />
        ) : (
          <>
            <Stack.Screen name="Main" component={UserTabs} options={{ title: '' }} />
            <Stack.Screen
              name="ProfileDetail"
              component={ProfileDetailScreen}
              options={{ headerShown: true, title: 'Profile', headerTintColor: COLORS.active }}
            />
            <Stack.Screen
              name="EditProfile"
              component={EditProfileScreen}
              options={{ headerShown: true, title: 'Edit Profile', headerTintColor: COLORS.active }}
            />
            <Stack.Screen
              name="Photo"
              component={PhotoScreen}
              options={{ headerShown: true, title: 'Profile Photo', headerTintColor: COLORS.active }}
            />
            <Stack.Screen
              name="Preferences"
              component={PreferencesScreen}
              options={{ headerShown: true, title: 'Partner Preferences', headerTintColor: COLORS.active }}
            />
            <Stack.Screen
              name="Help"
              component={HelpScreen}
              options={{ headerShown: true, title: 'Help & Support', headerTintColor: COLORS.active }}
            />
            <Stack.Screen
              name="BlockedProfiles"
              component={BlockedProfilesScreen}
              options={{ headerShown: true, title: 'Blocked Profiles', headerTintColor: COLORS.active }}
            />
          </>
        )}
      </Stack.Navigator>
    </NavigationContainer>
  );
}

const s = StyleSheet.create({
  adminBlock: {
    flex: 1, alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#FFF5F7', paddingHorizontal: 32, gap: 16,
  },
  adminTitle: { fontSize: 24, fontWeight: '900', color: '#1A1A2E' },
  adminSub: { fontSize: 15, color: '#666', textAlign: 'center', lineHeight: 24 },
  adminLogout: { marginTop: 8, fontSize: 14, color: '#aaa', textDecorationLine: 'underline' },
  reloginBtn: {
    marginTop: 8, paddingVertical: 14, paddingHorizontal: 40,
    borderRadius: 14, backgroundColor: COLORS.active,
  },
  reloginBtnText: { color: '#fff', fontWeight: '800', fontSize: 16 },
});
